import { useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  loadBookmarks,
  openSaveDialog,
  openEditDialog,
  openDeleteConfirm,
  closeDeleteConfirm,
  removeBookmark,
  clearError,
} from '../store/slices/bookmarksSlice';
import { setMapView, setMapExtent } from '../store/slices/mapSlice';
import type { Bookmark } from '../types/bookmark';

/**
 * BookmarksPanel - Displays a list of user bookmarks with actions to
 * create, select (restore view), edit, and delete bookmarks.
 */
export function BookmarksPanel() {
  const dispatch = useAppDispatch();
  const { bookmarks, isLoading, error, deleteConfirmOpen, deletingBookmarkId } = useAppSelector(
    (state) => state.bookmarks
  );
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadBookmarks());
    }
  }, [dispatch, isAuthenticated]);

  const handleBookmarkClick = (bookmark: Bookmark) => {
    // Restore the saved map view
    dispatch(
      setMapView({
        center: bookmark.center,
        zoom: bookmark.zoom,
      })
    );
    dispatch(
      setMapExtent({
        minX: bookmark.extent[0],
        minY: bookmark.extent[1],
        maxX: bookmark.extent[2],
        maxY: bookmark.extent[3],
      })
    );
  };

  const handleEdit = (bookmark: Bookmark) => {
    dispatch(openEditDialog(bookmark));
  };

  const handleDeleteClick = (bookmarkId: string) => {
    dispatch(openDeleteConfirm(bookmarkId));
  };

  const handleDeleteConfirm = () => {
    if (deletingBookmarkId) {
      dispatch(removeBookmark(deletingBookmarkId));
    }
  };

  const handleDeleteCancel = () => {
    dispatch(closeDeleteConfirm());
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Sign in to use spatial bookmarks.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with add button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Bookmarks
        </Typography>
        <Tooltip title="Save current view as bookmark">
          <IconButton
            size="small"
            color="primary"
            onClick={() => dispatch(openSaveDialog())}
            aria-label="Add bookmark"
          >
            <BookmarkAddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && bookmarks.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && bookmarks.length === 0 && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No bookmarks yet.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BookmarkAddIcon />}
            onClick={() => dispatch(openSaveDialog())}
          >
            Save Current View
          </Button>
        </Box>
      )}

      {/* Bookmarks list */}
      {bookmarks.length > 0 && (
        <List dense sx={{ overflow: 'auto', flex: 1 }}>
          {bookmarks.map((bookmark) => (
            <ListItem
              key={bookmark.id}
              disablePadding
              secondaryAction={
                <Box>
                  <Tooltip title="Edit bookmark">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleEdit(bookmark)}
                      aria-label={`Edit ${bookmark.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete bookmark">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDeleteClick(bookmark.id)}
                      aria-label={`Delete ${bookmark.name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemButton onClick={() => handleBookmarkClick(bookmark)}>
                <ListItemText
                  primary={bookmark.name}
                  secondary={bookmark.description || `Zoom: ${bookmark.zoom}`}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Bookmark</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this bookmark? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
